/**
 * SumCheckBanner — the §5 reconciliation verdict on every drill response.
 * Green: Σ rows == cell, BOTH numbers shown (the §5 2 dp rule already ran on
 * the backend; this renders its result, it never re-decides). Loud red when
 * false: constitution §3 says a non-reconciling drill is a bug — so it must
 * LOOK like one, never like a cosmetic footnote.
 */

import React from 'react'
import { CheckCircle2, XCircle } from 'lucide-react'
import { formatCurrency } from '../../utils/formatters'
import type { SumCheck } from '../../types/metrics'

const gbp = (value: number): string => formatCurrency(value, '£', 2)

/**
 * Optional unit formatter. Defaults to £ because every drill until A4 was money; the
 * Loading vs Capacity drill reconciles HOURS, and rendering "£1,610.00" for 1,610 hours
 * would make a correct reconciliation look like nonsense. The verdict logic is unchanged —
 * only the rendering of the two numbers moves.
 */
export type SumFormat = (value: number) => string

function RedState({ sumCheck, fmt }: { sumCheck: SumCheck; fmt: SumFormat }): React.JSX.Element {
  const delta = sumCheck.rows_total - sumCheck.cell_value
  return (
    <div
      role="alert"
      data-testid="sum-check"
      data-reconciles="false"
      className="flex items-start gap-2 rounded-xl border-2 border-bad bg-bad-bg px-3 py-2.5"
    >
      <XCircle size={18} className="mt-0.5 flex-shrink-0 text-bad" />
      <div>
        <p className="text-sm font-bold uppercase tracking-wide text-bad">
          Does not reconcile — Σ rows {fmt(sumCheck.rows_total)} ≠ cell{' '}
          {fmt(sumCheck.cell_value)} (Δ {fmt(delta)})
        </p>
        <p className="mt-0.5 text-xs text-bad">
          A drill list that does not sum to its cell is a bug, not a footnote (constitution §3).
          Do not trust this list until the mismatch is explained.
        </p>
      </div>
    </div>
  )
}

export default function SumCheckBanner({
  sumCheck,
  format,
}: {
  sumCheck: SumCheck
  format?: SumFormat
}): React.JSX.Element {
  const fmt = format ?? gbp
  if (!sumCheck.reconciles) return <RedState sumCheck={sumCheck} fmt={fmt} />
  return (
    <div
      role="status"
      data-testid="sum-check"
      data-reconciles="true"
      className="flex items-center gap-2 rounded-xl border border-good bg-good-bg px-3 py-1.5 text-xs font-medium text-good"
    >
      <CheckCircle2 size={14} className="flex-shrink-0" />
      <span>
        Reconciles — Σ rows {fmt(sumCheck.rows_total)} = cell {fmt(sumCheck.cell_value)}
      </span>
    </div>
  )
}
